import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { EstablishmentSwitcherComponent } from 'app/layout/common/establishment-switcher/establishment-switcher.component';
import { InformationBarComponent } from 'app/modules/shared/components/information-bar/information-bar.component';
import { Subject, takeUntil } from 'rxjs';
import { environment } from 'environments/environment';
import { TranslocoModule } from '@ngneat/transloco';
import { PathEnum } from 'app/modules/shared/enums/pathEnum';
import { EstablishmentService } from 'app/modules/features/services/establishment.service';
import { Establishment } from 'app/modules/features/models/establishment';
import { ImpersonationBannerComponent } from 'app/layout/common/impersonation-banner/impersonation-banner.component';
import { PaymentFailedBannerComponent } from 'app/layout/common/payment-failed-banner/payment-failed-banner.component';
import { SubscriptionService } from 'app/modules/partner/services/subscription.service';
import { Subscription } from 'app/modules/shared/models/subscription';
import { PartnerMenuService } from 'app/modules/partner/services/partner-menu.service';
import { PlanEnum } from 'app/modules/shared/enums/planEnum';
import { Plan } from 'app/modules/shared/models/plan';
import { PlanUtil } from 'app/modules/shared/utils/plan.util';
import { UpgradeService } from 'app/modules/shared/services/upgrade.service';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        NotificationsComponent,
        UserComponent,
        EstablishmentSwitcherComponent,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        LanguagesComponent,
        FuseFullscreenComponent,
        SearchComponent,
        ShortcutsComponent,
        MessagesComponent,
        RouterOutlet,
        QuickChatComponent,
        TranslocoModule,
        ImpersonationBannerComponent,
        PaymentFailedBannerComponent,
        InformationBarComponent
    ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    user: User;
    establishment: Establishment;
    previewUrl: string;
    subscription: Subscription;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _establishmentService: EstablishmentService,
        private _subscriptionService: SubscriptionService,
        private _partnerMenuService: PartnerMenuService,
        private _upgradeService: UpgradeService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    /**
     * Check if subscription is in trial period
     */
    get isTrialPeriod(): boolean {
        return this.subscription && this.subscription.subscriptionStatusRefCode === 'TRL';
    }

    /**
     * Calculate remaining trial days
     */
    get trialDaysRemaining(): number {
        if (!this.subscription || !this.subscription.trialPeriodEndDate) {
            return 0;
        }
        
        const endDate = new Date(this.subscription.trialPeriodEndDate);
        const currentDate = new Date();
        const timeDifference = endDate.getTime() - currentDate.getTime();
        const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));
        
        return Math.max(0, daysDifference);
    }

    /**
     * Get trial text with proper pluralization
     */
    get trialText(): string {
        const days = this.trialDaysRemaining;
        if (days === 1) {
            return 'features.common.trial.remaining_day_singular';
        } else {
            return 'features.common.trial.remaining_days_plural';
        }
    }

    /**
     * Check if the upgrade button should be shown (non-PRO users who are not in trial)
     */
    get showUpgradeButton(): boolean {
        if (!this.subscription) return false;
        const planCode = this.subscription.planCode;
        return planCode && planCode !== PlanEnum.PRO;
    }

    /**
     * Navigate to subscription settings
     */
    navigateToSubscription(): void {
        this._router.navigate([`/${PathEnum.APP}/${PathEnum.APP_PARTNER_SETTINGS}/${PathEnum.APP_PARTNER_SETTINGS_SUBSCRIPTION}`]);
    }

    /**
     * Open the plan selection modal directly from the navbar
     */
    navigateToUpgrade(): void {
        this._upgradeService.openUpgradeModal();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        // Subscribe to establishment changes
        this._establishmentService.establishment$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((establishment: Establishment) => {
                this.establishment = establishment;
                if (establishment?.uuid) {
                    this.previewUrl = this._partnerMenuService.generateMenuUrl(establishment, true);
                }
            });

        // Subscribe to subscription changes
        this._subscriptionService.partnerSubscription$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((subscription: Subscription) => {
                this.subscription = subscription;
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }
}
